// First Created - 08 Oct 2026
// Author - Atul Pandey
// Purpose - To deactivate or hard delete an
//           Org Trail Session Content Assignment

import React, {
    useState,
} from "react"

import {
    useNavigate,
} from "react-router"

import useTitle from "@/hooks/useTitle"

import EtBtnFormBasic
    from "@/components/common/buttons/EtBtnFormBasic"

import PulseLoader
    from "react-spinners/PulseLoader"

import Modal
    from "@/components/common/modal/Modal"

import {
    useGetOrganizationsQuery,
} from "@/pages/admin/organizations/organizationsApiSlice"

import {
    useGetTrailsQuery,
} from "@/features/trails/trailsApiSlice"

import {
    useGetSessionsQuery,
} from "@/features/sessions/sessionsApiSlice"

import {
    useGetOrgTrailSessionCohortsQuery,
} from "@/pages/admin/orgTrailSessionCohorts/orgTrailSessionCohortsApiSlice"

import {
    useGetOrgTrailSessionContentAssignmentsQuery,
    useUpdateOrgTrailSessionContentAssignmentMutation,
    useDeleteOrgTrailSessionContentAssignmentMutation,
} from "./orgTrailSessionContentAssignmentsApiSlice"


/*
|--------------------------------------------------------------------------
| 01 - Helpers
|--------------------------------------------------------------------------
*/

const getRefId = value =>
    String(value?._id || value?.id || value || "")

const toList = data =>
    data?.ids?.map(id => data.entities[id]).filter(Boolean) || []


/*
|--------------------------------------------------------------------------
| 02 - Main Component
|--------------------------------------------------------------------------
*/

const EtDeleteOrgTrailSessionContentAssignment = ({
    id,
    setActiveAdminView,
}) => {

    useTitle("Delete Session Content Assignment")

    const {
        assignment,
        isLoading,
        isFetching,
        isError,
        error,
    } = useGetOrgTrailSessionContentAssignmentsQuery(
        {},
        {
            selectFromResult: ({
                data,
                isLoading,
                isFetching,
                isError,
                error,
            }) => ({
                assignment: data?.entities[id],
                isLoading,
                isFetching,
                isError,
                error,
            }),
        }
    )

    if (isLoading || isFetching) {
        return <PulseLoader color="#ea580c" />
    }

    if (isError) {
        return (
            <p className="text-sm text-orange-700">
                {error?.data?.message ||
                    "Unable to load assignment."}
            </p>
        )
    }

    if (!assignment) {
        return (
            <p className="text-sm text-gray-600 p-3">
                Assignment not found.
            </p>
        )
    }

    return (

        <EtDeleteOrgTrailSessionContentAssignmentForm
            key={id}
            assignment={assignment}
            id={id}
            setActiveAdminView={setActiveAdminView}
        />

    )
}


/*
|--------------------------------------------------------------------------
| 03 - Delete Form
|--------------------------------------------------------------------------
*/

const EtDeleteOrgTrailSessionContentAssignmentForm = ({
    assignment,
    id,
    setActiveAdminView,
}) => {

    const navigate = useNavigate()

    const [
        isModalOpen,
        setIsModalOpen,
    ] = useState(false)

    const [
        actionMode,
        setActionMode,
    ] = useState("inactive")


    /*
    |--------------------------------------------------------------------------
    | 04 - Mutations
    |--------------------------------------------------------------------------
    */

    const [
        updateOrgTrailSessionContentAssignment,
        {
            isLoading: isUpdating,
            isError: isUpdateError,
            error: updateError,
        },
    ] = useUpdateOrgTrailSessionContentAssignmentMutation()

    const [
        deleteOrgTrailSessionContentAssignment,
        {
            isLoading: isDeleting,
            isError: isDeleteError,
            error: deleteError,
        },
    ] = useDeleteOrgTrailSessionContentAssignmentMutation()

    const isLoading = isUpdating || isDeleting

    const isError = isUpdateError || isDeleteError

    const error = updateError || deleteError


    /*
    |--------------------------------------------------------------------------
    | 05 - Master Queries For Names
    |--------------------------------------------------------------------------
    */

    const {
        data: organizationsData,
    } = useGetOrganizationsQuery("organizationsList")

    const {
        data: trailsData,
    } = useGetTrailsQuery("trailsList")

    const {
        data: sessionsData,
    } = useGetSessionsQuery("sessionsList")

    const {
        data: cohortsData,
    } = useGetOrgTrailSessionCohortsQuery({})


    const resolveName = (data, ref, field) => {

        if (ref?.[field]) {
            return ref[field]
        }

        const record = toList(data).find(
            item => getRefId(item) === getRefId(ref)
        )

        return record?.[field] || "Not defined"
    }


    /*
    |--------------------------------------------------------------------------
    | 06 - Modal Actions
    |--------------------------------------------------------------------------
    */

    const handleOpenModal = mode => {

        setActionMode(mode)
        setIsModalOpen(true)

    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
    }


    /*
    |--------------------------------------------------------------------------
    | 07 - Return To List
    |--------------------------------------------------------------------------
    */

    const returnToList = () => {

        if (setActiveAdminView) {

            setActiveAdminView(
                "orgTrailSessionContentAssignments"
            )

        } else {

            navigate(
                "/orgTrailSessionContentAssignments"
            )

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 08 - Confirm Action
    |--------------------------------------------------------------------------
    */

    const handleConfirm = async e => {

        e?.preventDefault?.()

        try {

            if (actionMode === "hard") {

                await deleteOrgTrailSessionContentAssignment({
                    id,
                }).unwrap()

                alert(
                    "Session Content assignment permanently deleted"
                )

                returnToList()
                return
            }

            await updateOrgTrailSessionContentAssignment({
                id,
                active: false,
            }).unwrap()

            alert(
                "Session Content assignment made inactive successfully"
            )

            returnToList()

        } catch (submitError) {

            console.error(submitError)

            alert(
                submitError?.data?.message ||
                (
                    actionMode === "hard"
                        ? "Failed to permanently delete Session Content assignment"
                        : "Failed to deactivate Session Content assignment"
                )
            )

        } finally {

            handleCloseModal()

        }

    }


    /*
    |--------------------------------------------------------------------------
    | 09 - Read Only Field
    |--------------------------------------------------------------------------
    */

    const ReadOnlyField = ({
        label,
        value,
    }) => (

        <div className="bg-white rounded m-1 p-2">

            <p className="font-bold text-xs m-1">
                {label}
            </p>

            <input
                type="text"
                value={value || "Not defined"}
                readOnly
                className="w-full rounded p-2 m-1 bg-gray-100 text-gray-600 cursor-not-allowed"
            />

        </div>

    )


    /*
    |--------------------------------------------------------------------------
    | 10 - Render
    |--------------------------------------------------------------------------
    */

    return (

        <div className="bg-gray-200 p-1 w-3/4 mx-auto mt-5 rounded-lg shadow-md shadow-black">

            {isLoading && (
                <PulseLoader color="#ea580c" />
            )}

            {isError && (

                <p className="text-orange-700 py-2">
                    {error?.data?.message}
                </p>

            )}

            <div className="bg-white p-1 m-1 rounded">

                <p className="p-1 m-1 font-bold font-poppins text-xl">
                    Delete Session Content Assignment
                </p>

                <div className="bg-gray-200 rounded m-1 p-1">

                    <form
                        onSubmit={e => {
                            e.preventDefault()
                            handleOpenModal("inactive")
                        }}
                        className="m-1 p-1 text-xs font-poppins"
                    >

                        <ReadOnlyField
                            label="Organization"
                            value={resolveName(
                                organizationsData,
                                assignment.orgId,
                                "organizationName"
                            )}
                        />

                        <ReadOnlyField
                            label="Trail"
                            value={resolveName(
                                trailsData,
                                assignment.trailId,
                                "trailName"
                            )}
                        />

                        <ReadOnlyField
                            label="Cohort"
                            value={resolveName(
                                cohortsData,
                                assignment.cohortId,
                                "cohortName"
                            )}
                        />

                        <ReadOnlyField
                            label="Session"
                            value={resolveName(
                                sessionsData,
                                assignment.sessionId,
                                "sessionName"
                            )}
                        />

                        <ReadOnlyField
                            label="Reference Content"
                            value={
                                assignment.referenceContentId?.title ||
                                "Reference Content not defined"
                            }
                        />

                        <ReadOnlyField
                            label="Content Type"
                            value={
                                assignment.referenceContentId?.contentType ||
                                "Not defined"
                            }
                        />

                        <ReadOnlyField
                            label="Current Assignment"
                            value={
                                assignment.active !== false
                                    ? "Active"
                                    : "Inactive"
                            }
                        />


                        {/* Warning */}

                        <div className="bg-orange-50 border border-orange-200 rounded m-1 p-2">

                            <p className="text-xs font-bold text-orange-700">
                                Choose how this Content assignment should
                                be removed from active use.
                            </p>

                            <p className="text-[10px] text-orange-700 mt-1">
                                Make Inactive preserves the assignment
                                but stops this reference content from
                                being visible through this cohort assignment.
                                Hard Delete permanently removes the assignment,
                                without deleting the master reference content.
                            </p>

                        </div>


                        {/* Actions */}

                        <div className="bg-white m-1 px-1 py-3 rounded">

                            <EtBtnFormBasic
                                name="Make Inactive"
                                type="clear"
                                handleClick={() =>
                                    handleOpenModal("inactive")
                                }
                            />

                            <EtBtnFormBasic
                                name="Hard Delete"
                                type="create"
                                handleClick={() =>
                                    handleOpenModal("hard")
                                }
                            />

                            <EtBtnFormBasic
                                name="Cancel"
                                type="cancel"
                                handleClick={returnToList}
                            />

                        </div>

                    </form>

                </div>

            </div>


            {/* Confirmation Modal */}

            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleConfirm}
            />

        </div>

    )
}


export default EtDeleteOrgTrailSessionContentAssignment